import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Row, Col, Card, Table, Badge, Button, Modal,
  Alert, Spinner, Form,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { getIntakeReviews, getIntakeReview, resolveIntakeReview } from "../intakeApi";
import { searchPatients } from "../api";

const REASON_LABELS = {
  DUPLICATE_PATIENT_ID: "Duplicate patient ID",
  DUPLICATE_PATIENT_DOB_EMAIL: "Duplicate DOB + email",
  DUPLICATE_PATIENT_DOB_NAME: "Duplicate DOB + name",
  DUPLICATE_ACCOUNT_ID: "Duplicate account ID",
  DUPLICATE_MEDICAL_AID_NUMBER: "Duplicate medical aid number",
  NO_PATIENT_MATCH: "No patient found",
  MULTIPLE_PATIENT_MATCHES: "Multiple patient matches",
  NO_ACCOUNT_MATCH: "No account found",
  MULTIPLE_ACCOUNT_MATCHES: "Multiple account matches",
  FAMILYCHECK_FAILED: "FamilyCheck failed",
  FAMILYCHECK_NO_DEPENDANTS: "FamilyCheck — no dependants",
  PATIENT_ALREADY_LINKED: "Patient already linked",
};

const FLOW_LABELS = {
  A: "New + Medical Aid",
  B: "New + Private",
  C: "Existing + Medical Aid",
  D: "Existing + Private",
};

export function IntakeReviews() {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [page, setPage] = useState(1);

  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewDetail, setReviewDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [resolving, setResolving] = useState(false);
  const [resolveAction, setResolveAction] = useState("");
  const [resolvePatientId, setResolvePatientId] = useState("");
  const [resolveAccountId, setResolveAccountId] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [resolveSuccess, setResolveSuccess] = useState(false);

  // Patient search for the "created" / "linked" actions
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getIntakeReviews(statusFilter, page);
      setReviews(data.reviews || []);
      setTotal(data.total || 0);
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  // Deep link — dashboard INTAKE_REVIEW tasks navigate here with
  // ?reviewId=<id> so the review modal opens without hunting the list.
  // The param is consumed (removed) once handled so closing the modal
  // doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const rid = searchParams.get("reviewId");
    if (!rid) return;
    (async () => {
      try {
        const detail = await getIntakeReview(rid);
        if (detail && detail._id) openReview(detail);
      } catch {
        // Unknown / inaccessible review — fall back to the plain list.
      }
      const next = new URLSearchParams(searchParams);
      next.delete("reviewId");
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openReview(review) {
    setSelectedReview(review);
    setReviewDetail(null);
    setResolveAction("");
    setResolvePatientId("");
    setResolveAccountId("");
    setResolveError("");
    setResolveSuccess(false);
    setPatientSearchQuery("");
    setPatientSearchResults([]);
    setSelectedPatient(null);
    setDetailLoading(true);
    try {
      const detail = await getIntakeReview(review._id);
      setReviewDetail(detail);
    } catch {
      setReviewDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function runPatientSearch(query) {
    setPatientSearchQuery(query);
    if (!query || query.trim().length < 2) {
      setPatientSearchResults([]);
      return;
    }
    setPatientSearchLoading(true);
    try {
      const token = sessionStorage.getItem("user");
      const decoded = token ? jwtDecode(token) : {};
      const practices = Array.isArray(decoded.practice) ? decoded.practice : [];
      const results = await searchPatients(query.trim(), practices);
      setPatientSearchResults(Array.isArray(results) ? results : []);
    } finally {
      setPatientSearchLoading(false);
    }
  }

  function pickPatient(p) {
    setSelectedPatient(p);
    setResolvePatientId(String(p._id));
    setPatientSearchResults([]);
    setPatientSearchQuery(`${p.patientFirstName || ""} ${p.patientLastName || ""}`.trim());
  }

  async function handleResolve() {
    if (!resolveAction) { setResolveError("Please choose an action."); return; }
    if ((resolveAction === "created" || resolveAction === "linked") && !resolvePatientId) {
      setResolveError("Please search for and select the patient so the submitted form (including any medical history captured) can be attached to their record.");
      return;
    }
    setResolveError("");
    setResolving(true);
    try {
      await resolveIntakeReview(
        selectedReview._id,
        resolveAction,
        resolvePatientId || null,
        resolveAccountId || null
      );
      setResolveSuccess(true);
      load();
    } catch (err) {
      setResolveError(err?.response?.data?.error || "Failed to resolve. Please try again.");
    } finally {
      setResolving(false);
    }
  }

  function closeModal() {
    setSelectedReview(null);
    setReviewDetail(null);
    setResolveSuccess(false);
  }

  function formatDate(d) {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Intake Reviews</h4>
          <div className="text-muted small">Submissions that require staff review</div>
        </Col>
        <Col xs="auto">
          <Form.Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: 160 }}
          >
            <option value="PENDING">Pending</option>
            <option value="RESOLVED">Resolved</option>
            <option value="DISMISSED">Dismissed</option>
          </Form.Select>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center text-muted py-5">
              No {statusFilter.toLowerCase()} reviews.
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Practice</th>
                  <th>Flow</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r) => (
                  <tr key={r._id}>
                    <td className="small">{formatDate(r.createdAt)}</td>
                    <td className="small">{r.practiceNumber}</td>
                    <td>
                      <Badge bg="secondary">{FLOW_LABELS[r.flowType] || r.flowType}</Badge>
                    </td>
                    <td className="small">{REASON_LABELS[r.reason] || r.reason}</td>
                    <td>
                      <Badge bg={r.status === "PENDING" ? "warning" : r.status === "RESOLVED" ? "success" : "secondary"}>
                        {r.status}
                      </Badge>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => openReview(r)}>
                        {r.status === "PENDING" ? "Review" : "View"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {total > 50 && (
          <Card.Footer className="d-flex justify-content-between align-items-center small text-muted">
            <span>Showing {reviews.length} of {total}</span>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button size="sm" variant="outline-secondary" disabled={reviews.length < 50} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* Review detail modal */}
      <Modal show={!!selectedReview} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Intake Review — {selectedReview && (FLOW_LABELS[selectedReview.flowType] || selectedReview.flowType)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : resolveSuccess ? (
            <Alert variant="success">Review resolved successfully.</Alert>
          ) : reviewDetail ? (
            <>
              <Alert variant="warning" className="small py-2">
                <strong>Reason:</strong> {REASON_LABELS[reviewDetail.reason] || reviewDetail.reason}
              </Alert>

              {/* Submitted data */}
              <h6>Submitted Data</h6>
              <Table size="sm" bordered className="mb-4">
                <tbody>
                  {reviewDetail.submittedData?.patient && Object.entries(reviewDetail.submittedData.patient)
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <tr key={k}>
                        <td className="text-muted small" style={{ width: "40%" }}>{k}</td>
                        <td className="small">{v}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>

              {/* Candidate patients */}
              {reviewDetail.candidatePatients?.length > 0 && (
                <>
                  <h6>Candidate Patients</h6>
                  <Table size="sm" bordered className="mb-4">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th><th>DOB</th><th>ID Number</th><th>Email</th><th>ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewDetail.candidatePatients.map((p) => (
                        <tr key={p._id}>
                          <td className="small">{p.patientFirstName} {p.patientLastName}</td>
                          <td className="small">{p.patientDOB}</td>
                          <td className="small">{p.patientIdNumber}</td>
                          <td className="small">{p.patientEmail}</td>
                          <td className="small text-muted" style={{ fontSize: "0.7rem" }}>{p._id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {/* Candidate accounts */}
              {reviewDetail.candidateAccounts?.length > 0 && (
                <>
                  <h6>Candidate Accounts</h6>
                  <Table size="sm" bordered className="mb-4">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th><th>DOB</th><th>ID Number</th><th>Email</th><th>ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewDetail.candidateAccounts.map((a) => (
                        <tr key={a._id}>
                          <td className="small">{a.accountFirstName} {a.accountLastName}</td>
                          <td className="small">{a.accountDOB}</td>
                          <td className="small">{a.accountIdNumber}</td>
                          <td className="small">{a.accountEmail}</td>
                          <td className="small text-muted" style={{ fontSize: "0.7rem" }}>{a._id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {/* Resolution actions (only for PENDING) */}
              {selectedReview?.status === "PENDING" && (
                <>
                  <hr />
                  <h6>Resolve</h6>
                  {resolveError && <Alert variant="danger" className="small py-2">{resolveError}</Alert>}

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Action</Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {[
                        ["created", "Mark as Created", "success"],
                        ["linked", "Link to Existing", "primary"],
                        ["dismissed", "Dismiss", "secondary"],
                      ].map(([val, lbl, variant]) => (
                        <Button
                          key={val}
                          size="sm"
                          variant={resolveAction === val ? variant : `outline-${variant}`}
                          onClick={() => setResolveAction(val)}
                        >
                          {lbl}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>

                  {(resolveAction === "created" || resolveAction === "linked") && (
                    <>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-semibold">
                          {resolveAction === "created"
                            ? "Attach to newly-created patient"
                            : "Attach to existing patient"}
                        </Form.Label>
                        <div className="text-muted small mb-2">
                          Search by name or ID number. Selecting a patient attaches the submitted
                          intake form (including any medical history captured) to their record.
                        </div>
                        <div className="position-relative">
                          <Form.Control
                            size="sm"
                            placeholder="Type to search…"
                            value={patientSearchQuery}
                            onChange={(e) => {
                              setSelectedPatient(null);
                              setResolvePatientId("");
                              runPatientSearch(e.target.value);
                            }}
                          />
                          {patientSearchLoading && (
                            <Spinner
                              size="sm"
                              animation="border"
                              className="position-absolute"
                              style={{ right: 8, top: 6 }}
                            />
                          )}
                          {patientSearchResults.length > 0 && !selectedPatient && (
                            <div
                              className="border rounded bg-white shadow-sm position-absolute w-100"
                              style={{ zIndex: 1050, maxHeight: 220, overflowY: "auto" }}
                            >
                              {patientSearchResults.map((p) => (
                                <div
                                  key={String(p._id)}
                                  className="px-2 py-1 small border-bottom"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => pickPatient(p)}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  <div className="fw-semibold">
                                    {p.patientFirstName} {p.patientLastName}
                                  </div>
                                  <div className="text-muted">
                                    {p.patientIdNumber || "no ID"}
                                    {p.patientNumber ? ` · ${p.patientNumber}` : ""}
                                    {p.patientDOB ? ` · DOB ${p.patientDOB}` : ""}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Form.Group>

                      {selectedPatient && (
                        <Alert variant="info" className="small py-2 d-flex align-items-center gap-2">
                          <span>
                            Selected:{" "}
                            <strong>
                              {selectedPatient.patientFirstName} {selectedPatient.patientLastName}
                            </strong>
                            {selectedPatient.patientIdNumber ? ` (${selectedPatient.patientIdNumber})` : ""}
                          </span>
                          <Button
                            size="sm"
                            variant="link"
                            className="ms-auto p-0"
                            onClick={() => {
                              setSelectedPatient(null);
                              setResolvePatientId("");
                              setPatientSearchQuery("");
                            }}
                          >
                            Change
                          </Button>
                        </Alert>
                      )}

                      {resolveAction === "linked" && (
                        <Form.Group className="mb-3">
                          <Form.Label className="small">Account ID (MongoDB _id)</Form.Label>
                          <Form.Control
                            size="sm"
                            placeholder="Paste account _id (optional — only needed to link patient to account)"
                            value={resolveAccountId}
                            onChange={(e) => setResolveAccountId(e.target.value)}
                          />
                        </Form.Group>
                      )}
                    </>
                  )}
                </>
              )}

              {selectedReview?.status !== "PENDING" && selectedReview?.resolution && (
                <Alert variant="secondary" className="small">
                  <strong>Resolved by:</strong> {selectedReview.resolution.resolvedBy} on{" "}
                  {formatDate(selectedReview.resolution.resolvedAt)} — Action: {selectedReview.resolution.action}
                </Alert>
              )}
            </>
          ) : (
            <Alert variant="danger">Failed to load review details.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Close</Button>
          {selectedReview?.status === "PENDING" && !resolveSuccess && !detailLoading && (
            <Button variant="primary" onClick={handleResolve} disabled={resolving || !resolveAction}>
              {resolving ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : "Save Resolution"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
